import React, { useState } from 'react';

import { useSnackbar } from 'notistack';

import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';

import { Tag, createOrAssignTags, removeTagForAdGroups } from '~/services/PublicOfferFeedService';

interface AdGroupTagsProps {
  customerId: string;
  campaignId: string;
  adGroupId: string;
  selectedTags: Array<string>;
  onTagsChange: (tags: Array<string>) => void;
  availableTags: Array<Tag>;
  onTagsRefresh?: () => void;
}

export default function AdGroupTags({
  customerId,
  campaignId,
  adGroupId,
  selectedTags,
  onTagsChange,
  availableTags,
  onTagsRefresh,
}: AdGroupTagsProps) {
  const { enqueueSnackbar } = useSnackbar();
  const [isAssigningTags, setIsAssigningTags] = useState(false);

  const handleTagsChange = async (newTags: Array<string>) => {
    setIsAssigningTags(true);
    try {
      const removedTags = selectedTags.filter((tag) => !newTags.includes(tag));
      const addedTags = newTags.filter((tag) => !selectedTags.includes(tag));

      if (removedTags.length > 0) {
        for (const removedTagName of removedTags) {
          const removedTag = availableTags.find((tag) => tag.tagName === removedTagName);
          if (removedTag?.tagId) {
            await removeTagForAdGroups(customerId, campaignId, removedTag.tagId.toString(), [adGroupId]);
          }
        }
      }

      if (addedTags.length > 0) {
        const tagsToAssign = addedTags.map((tagName) => {
          const existingTag = availableTags.find((tag) => tag.tagName === tagName);
          return {
            customerId,
            campaignId,
            adGroupId,
            tagId: existingTag?.tagId?.toString(),
            tagName,
          };
        });

        await createOrAssignTags({
          tags: tagsToAssign,
        });
      }

      enqueueSnackbar('Tags updated successfully', { variant: 'success' });

      if (onTagsRefresh) {
        onTagsRefresh();
      }
    } catch (error) {
      console.error('Error updating tags:', error);
      enqueueSnackbar('Failed to update tags', { variant: 'error' });
      onTagsChange(selectedTags);
    } finally {
      setIsAssigningTags(false);
    }
  };

  const handleTagCreation = (inputValue: string) => {
    const existingTag = availableTags.find((tag) => tag.tagName.toLowerCase() === inputValue.toLowerCase());

    if (existingTag) {
      enqueueSnackbar(`Tag "${inputValue}" already exists`, { variant: 'warning' });
      return false;
    }

    return true;
  };

  return (
    <FormControl sx={{ width: 300 }}>
      <Autocomplete
        multiple
        value={selectedTags}
        onChange={(_, newValue) => {
          const processedValues = newValue.map((value) => {
            if (value.startsWith('Create "')) {
              const tagName = value.replace('Create "', '').replace('"', '');
              return tagName;
            }
            return value;
          });

          onTagsChange(processedValues);
          handleTagsChange(processedValues);
        }}
        options={availableTags.map((tag) => tag.tagName)}
        filterSelectedOptions
        filterOptions={(options, { inputValue }) => {
          const filtered = options.filter((option) => option.toLowerCase().includes(inputValue.toLowerCase()));

          // Only allow creation if the tag doesn't already exist (case-insensitive)
          if (inputValue && !options.some((option) => option.toLowerCase() === inputValue.toLowerCase())) {
            // Check if we should allow creation
            if (handleTagCreation(inputValue)) {
              filtered.push(`Create "${inputValue}"`);
            }
          }

          return filtered;
        }}
        renderInput={(params) => <TextField {...params} placeholder="Type to search or create tags..." />}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => <Chip key={option} label={option} {...getTagProps({ index })} />)
        }
        renderOption={(props, option) => {
          return (
            <Box component="li" {...props}>
              <span>{option}</span>
            </Box>
          );
        }}
        disabled={isAssigningTags}
        freeSolo
        selectOnFocus
        clearOnBlur={false}
        handleHomeEndKeys
      />
    </FormControl>
  );
}
